import { ButtonSecondary, Colors, Icon, Text } from '@amelio/ui-library';

import type { MeetingTranscriptLabels } from '../types';

import { PlanLockIcon, usePlanLock } from '@/components/ProGate/PlanLock';

interface TranscriptUploadPanelProps {
    labels: MeetingTranscriptLabels;
    isUploading: boolean;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    onChooseFile: () => void;
    onFileSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
    acceptedFileTypes: string;
    disabled?: boolean;
}

/** The invitation to import a transcript, shown while none exists. */
export const TranscriptUploadPanel = ({
    labels,
    isUploading,
    fileInputRef,
    onChooseFile,
    onFileSelected,
    acceptedFileTypes,
    disabled = false,
}: TranscriptUploadPanelProps) => {
    const planLock = usePlanLock();
    return (
        <div className={'flex w-full items-end justify-end'}>
            <div
                className={
                    'border-base-200 flex min-h-64 w-full flex-col items-center justify-center gap-4 rounded-xl border border-dashed bg-white/60 px-6 py-8 text-center sm:h-[250px] md:h-[300px]'
                }
                data-testid={'meeting-transcript-dropzone'}
            >
                <Icon
                    name={'lucide-sparkles'}
                    color={Colors.SECONDARY}
                    className={'!h-11 !w-11'}
                />

                <Text
                    size={'small'}
                    className={
                        '!from-ai-purple !to-ai-pink !bg-gradient-to-r !bg-clip-text !text-center !text-transparent !italic'
                    }
                >
                    {labels.uploadPrompt}
                </Text>

                <ButtonSecondary
                    onClick={planLock.isLocked ? planLock.onLocked : onChooseFile}
                    loading={!planLock.isLocked && isUploading}
                    disabled={!planLock.isLocked && disabled}
                    leftIcon={planLock.isLocked ? <PlanLockIcon /> : undefined}
                    size={'sm'}
                    fitContent
                    dataTestId={'choose-transcript-file-button'}
                    className={
                        'rounded-lg border !border-black bg-white/90 !px-5 !py-2 !text-black backdrop-blur-[30px] transition-colors hover:!scale-100 hover:bg-white disabled:opacity-50'
                    }
                >
                    {labels.chooseFile}
                </ButtonSecondary>

                <input
                    ref={fileInputRef}
                    type={'file'}
                    accept={acceptedFileTypes}
                    disabled={disabled}
                    className={'hidden'}
                    onChange={onFileSelected}
                    data-testid={'meeting-transcript-file-input'}
                />
            </div>
        </div>
    );
};
